import { createFileRoute } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { RelatedReading } from "@/components/RelatedReading";
import { relatedArticles } from "@/lib/clusters";
import { buildBlogHead } from "@/lib/blogMeta";
import manuscript from "@/content/diwali-healthy-gifts-ideas.md?raw";
import hero from "@/assets/blog/diwali-hero-1600.webp.asset.json";
import heroSmall from "@/assets/blog/diwali-hero-768.webp.asset.json";
import choose from "@/assets/blog/diwali-choose-1600.webp.asset.json";
import chooseSmall from "@/assets/blog/diwali-choose-768.webp.asset.json";
import comparison from "@/assets/blog/diwali-comparison-1600.webp.asset.json";
import comparisonSmall from "@/assets/blog/diwali-comparison-768.webp.asset.json";
import ova from "@/assets/blog/diwali-ova-1600.webp.asset.json";
import ovaSmall from "@/assets/blog/diwali-ova-768.webp.asset.json";
import faq from "@/assets/blog/diwali-faq-1600.webp.asset.json";
import faqSmall from "@/assets/blog/diwali-faq-768.webp.asset.json";

const SLUG = "diwali-healthy-gifts-ideas";
const TITLE = "Diwali Healthy Gifts Ideas for Family, Friends & Colleagues - TruWe";
const HEADLINE = "Diwali Healthy Gifts Ideas for Family, Friends & Colleagues";
const DESCRIPTION = "Diwali healthy gifts ideas for family, friends and colleagues. Explore unique, useful wellness gifts from TruWe, plus how to choose and who should check first.";
const PUBLISHED = "2026-10-05";
const images: Record<string, { url: string; small: string }> = {
  "diwali-choose": { url: choose.url, small: chooseSmall.url },
  "diwali-comparison": { url: comparison.url, small: comparisonSmall.url },
  "diwali-ova": { url: ova.url, small: ovaSmall.url },
  "diwali-faq": { url: faq.url, small: faqSmall.url },
};

export const Route = createFileRoute("/blog/diwali-healthy-gifts-ideas")({
  component: Article,
  head: () => {
    const base = buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: HEADLINE,
      ogDescription: DESCRIPTION,
      headline: HEADLINE,
      breadcrumbName: HEADLINE,
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      authorName: "TruWe",
      articleType: "BlogPosting",
      faqs: [],
      ogImage: `https://truwe.in${hero.url}`,
    });
    return {
      ...base,
      meta: [
        ...base.meta,
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://truwe.in${hero.url}` },
      ],
    };
  },
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <p className="text-xs font-semibold uppercase text-brand">Clinical Library · Festive Gifting Ideas</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-brand-deep md:text-5xl">{HEADLINE}</h1>
        <BlogByline author="TruWe" date={PUBLISHED} readingTime="10 min read" sources="British Heart Foundation and TruWe ingredient research; sources linked below." />
        <div className="mt-4"><ReadWithLlm title={HEADLINE} url={`https://truwe.in/blog/${SLUG}`} kind="article" /></div>
        <img
          src={hero.url}
          srcSet={`${heroSmall.url} 768w, ${hero.url} 1600w`}
          sizes="(max-width: 768px) 100vw, 768px"
          alt="Diwali healthy gift ideas with TruWe wellness products in a festive setting"
          width={1600}
          height={859}
          fetchPriority="high"
          decoding="async"
          className="mt-8 h-auto w-full rounded-md border border-border"
        />
        <div className="mt-8 text-base leading-8 text-foreground/85 md:text-[17px]">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            h2: ({ children }) => <h2 className="mb-4 mt-12 font-display text-2xl font-extrabold leading-tight text-brand-deep md:text-3xl">{children}</h2>,
            h3: ({ children }) => <h3 className="mb-3 mt-8 font-display text-xl font-bold text-brand-deep">{children}</h3>,
            p: ({ children }) => <p className="my-4">{children}</p>,
            ul: ({ children }) => <ul className="my-5 list-disc space-y-2 pl-6 marker:text-brand">{children}</ul>,
            li: ({ children }) => <li className="pl-1">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-brand-deep">{children}</strong>,
            a: ({ href, children }) => <a href={href} rel={href?.startsWith("http") ? "noopener noreferrer" : undefined} target={href?.startsWith("http") ? "_blank" : undefined} className="text-brand underline underline-offset-2 hover:text-brand-deep">{children}</a>,
            img: ({ src, alt }) => {
              const image = images[src ?? ""];
              if (!image) return null;
              return <img src={image.url} srcSet={`${image.small} 768w, ${image.url} 1600w`} sizes="(max-width: 768px) 100vw, 768px" alt={alt ?? ""} width={1600} height={859} loading="lazy" decoding="async" className="my-8 h-auto w-full rounded-md border border-border" />;
            },
            table: ({ children }) => <div className="my-6 overflow-x-auto border-y border-border"><table className="w-full min-w-[560px] border-collapse text-left text-sm leading-6">{children}</table></div>,
            th: ({ children }) => <th className="border-b border-border bg-muted/30 px-4 py-3 font-semibold text-brand-deep">{children}</th>,
            td: ({ children }) => <td className="border-b border-border px-4 py-3 align-top">{children}</td>,
          }}>{manuscript.replace(/^# .+\n/, "")}</ReactMarkdown>
        </div>
        <RelatedReading articles={relatedArticles(SLUG)} />
      </div>
    </article>
  );
}